import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RISK_FIELD_CATALOG, RISK_FIELD_SOURCES, } from '@wit-support/risk-alerts';
import { Button, Combobox, Input, Label, SearchableSelect, Select } from '@wit-support/ui';
import { RISK_ALERT_KINDS, RISK_ALERT_KIND_RU, RISK_AUTO_LINK_BASIS_RU, RISK_MATCH_CONFIDENCE_RU, RISK_NETWORKS, RISK_NETWORK_RU, } from '@wit-support/ui-domain';
import { Plus, X } from 'lucide-react';
/** Operators that ignore `value` — presence checks. `is_true`/`is_false` are
 * unreachable in practice (the catalog has no boolean field today) but kept
 * here so a future boolean field doesn't silently show a value input. */
const VALUELESS_OPERATORS = new Set(['exists', 'not_exists', 'is_true', 'is_false']);
const FIELD_OPERATOR_LABELS = {
    exists: 'заполнено',
    not_exists: 'не заполнено',
    equals: 'равно',
    not_equals: 'не равно',
    gt: 'больше',
    gte: 'больше или равно',
    lt: 'меньше',
    lte: 'меньше или равно',
    contains: 'содержит',
    in: 'входит в список',
    is_true: 'истина',
    is_false: 'ложь',
};
const FIELD_SOURCE_LABELS = {
    case: 'Кейс',
    alert: 'Алерт (сработавший)',
};
const RISK_MATCH_CONFIDENCE_VALUES = Object.keys(RISK_MATCH_CONFIDENCE_RU);
const RISK_AUTO_LINK_BASIS_VALUES = Object.keys(RISK_AUTO_LINK_BASIS_RU);
/**
 * Небольшой, но не полный список валют ISO 4217 — канонического справочника
 * валют в кодовой базе нет (`currencyScale` в `@wit-support/risk-source-
 * chargeblast` знает только исключения из «двух знаков после запятой», а не
 * полный список кодов). Список подсказывает частые значения; при этом сам
 * `case.currency` в каталоге типизирован как СТРОКА — правило по-прежнему
 * можно сохранить и с кодом не из этого списка (см. `EnumScalarSelect`
 * ниже — при уже сохранённом чужом значении список содержит его как
 * дополнительный, немаркированный пункт), просто ввод по умолчанию идёт
 * через выбор, а не свободный текст.
 */
const RISK_CURRENCY_CODES = [
    'USD',
    'EUR',
    'GBP',
    'AUD',
    'CAD',
    'CHF',
    'JPY',
    'CNY',
    'INR',
    'BRL',
    'MXN',
    'ZAR',
    'SGD',
    'HKD',
    'NZD',
    'SEK',
    'NOK',
    'DKK',
    'PLN',
    'TRY',
    'AED',
    'SAR',
    'ILS',
    'KRW',
    'THB',
    'PHP',
    'IDR',
    'MYR',
    'VND',
    'PYG',
    'CLP',
    'COP',
    'ARS',
    'PEN',
    'UAH',
    'CZK',
    'HUF',
    'RON',
];
/**
 * Каталог подсказанных значений по `(source, path)`. Изначально — четыре поля
 * из `RISK_FIELD_CATALOG`, перечисленные в брифе задачи 13; волна 4 добавила
 * ещё два (`alert.kind`, `case.match_basis`) по тому же доводу: опечатка в
 * свободном тексте здесь тихо делает правило вечно-несовпадающим, и без
 * подсказки её некому заметить, ведь правило проходит валидацию (это просто
 * строка) и молча не срабатывает НИКОГДА. `alert.kind` — ТОТ ЖЕ enum, что
 * `case.kind` (`RiskAlertKind`), поэтому переиспользует тот же словарь
 * `RISK_ALERT_KIND_RU`. `case.match_basis` — `RiskAutoLinkBasis`
 * (`network-id`/`code+card`/`code+token`/`code+amount`/`single-customer` —
 * пятое значение, `code+amount`, добавлено редакцией контракта поиска от
 * 2026-08-27), подписи из `riskAutoLinkBasisLabelRu`/`RISK_AUTO_LINK_BASIS_RU`
 * в `@wit-support/ui-domain` — того же словаря, что рисует бейдж
 * авто-привязки кандидата. Список значений `Select`'а — `RISK_AUTO_LINK_
 * BASIS_VALUES` ниже — выведен из ключей этого словаря, а не переписан
 * вторым списком, поэтому новое значение подхватывается сюда без правки этого
 * файла.
 */
const SUGGESTED_VALUES = {
    case: {
        kind: RISK_ALERT_KINDS.map((k) => ({ value: k, label: RISK_ALERT_KIND_RU[k] })),
        currency: RISK_CURRENCY_CODES.map((c) => ({ value: c, label: c })),
        match_confidence: RISK_MATCH_CONFIDENCE_VALUES.map((c) => ({
            value: c,
            label: RISK_MATCH_CONFIDENCE_RU[c],
        })),
        match_basis: RISK_AUTO_LINK_BASIS_VALUES.map((b) => ({
            value: b,
            label: RISK_AUTO_LINK_BASIS_RU[b],
        })),
    },
    alert: {
        network: RISK_NETWORKS.map((n) => ({ value: n, label: RISK_NETWORK_RU[n] })),
        kind: RISK_ALERT_KINDS.map((k) => ({ value: k, label: RISK_ALERT_KIND_RU[k] })),
    },
};
function suggestedValuesFor(source, path) {
    return SUGGESTED_VALUES[source]?.[path];
}
const NONE = '__none__';
const defaultFieldNode = () => {
    const firstField = RISK_FIELD_CATALOG.case[0];
    return {
        type: 'field',
        source: 'case',
        path: firstField?.path ?? '',
        operator: 'equals',
    };
};
/**
 * Короткая читаемая сводка дерева условий — для строки списка правил
 * (`routes/rules.tsx`), где полный редактор не помещается. `field`-лист
 * читает подпись поля из того же каталога, что и сам редактор, поэтому
 * сводка и редактор не могут разъехаться в именовании полей.
 */
export function summarizeReactionExpr(expr) {
    switch (expr.type) {
        case 'always':
            return 'Всегда (без условия)';
        case 'field': {
            const def = RISK_FIELD_CATALOG[expr.source].find((f) => f.path === expr.path);
            const fieldLabel = def?.label ?? expr.path;
            const opLabel = FIELD_OPERATOR_LABELS[expr.operator] ?? expr.operator;
            const valueLabel = summarizeExprValue(expr.value);
            return `${fieldLabel} ${opLabel}${valueLabel ? ` ${valueLabel}` : ''}`;
        }
        case 'all':
            return expr.of.map(summarizeReactionExpr).join(' И ');
        case 'any':
            return expr.of.map(summarizeReactionExpr).join(' ИЛИ ');
        case 'not':
            return `НЕ (${summarizeReactionExpr(expr.of)})`;
    }
}
function summarizeExprValue(v) {
    if (v === undefined)
        return '';
    if (Array.isArray(v))
        return v.join(', ');
    return String(v);
}
export function ReactionConditionEditor({ value, onChange }) {
    if (value.type === 'always') {
        return (_jsxs("div", { className: "flex flex-col gap-2 rounded-[var(--radius-3)] border border-dashed border-[var(--color-border-default)] p-3", children: [_jsx("p", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0421\u0440\u0430\u0431\u0430\u0442\u044B\u0432\u0430\u0435\u0442 \u0434\u043B\u044F \u041B\u042E\u0411\u041E\u0413\u041E \u043A\u0435\u0439\u0441\u0430, \u0434\u043E\u0448\u0435\u0434\u0448\u0435\u0433\u043E \u0434\u043E \u0440\u0435\u0430\u043A\u0442\u043E\u0440\u0430 (\u0431\u0435\u0437 \u0443\u0441\u043B\u043E\u0432\u0438\u044F)." }), _jsx("div", { children: _jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => onChange({ type: 'all', of: [defaultFieldNode()] }), children: [_jsx(Plus, { className: "h-4 w-4" }), " \u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0443\u0441\u043B\u043E\u0432\u0438\u0435"] }) })] }));
    }
    return _jsx(Node, { node: value, depth: 0, onChange: (next) => onChange(next ?? { type: 'always' }) });
}
function Node({ node, depth, onChange }) {
    if (node.type === 'always') {
        // Not reachable from this editor's own controls below the root (only the
        // top-level `ReactionConditionEditor` renders the `always` state) — kept
        // as a safety net for a tree edited by hand or migrated from elsewhere.
        return (_jsxs("div", { className: "flex items-center justify-between gap-2 rounded-[var(--radius-3)] border border-[var(--color-border-default)] p-3", children: [_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0412\u0441\u0435\u0433\u0434\u0430 (\u0431\u0435\u0437 \u0443\u0441\u043B\u043E\u0432\u0438\u044F)" }), _jsx(DeleteButton, { onClick: () => onChange(null), label: "\u0423\u0431\u0440\u0430\u0442\u044C" })] }));
    }
    if (node.type === 'field') {
        return _jsx(FieldLeaf, { node: node, onChange: onChange });
    }
    if (node.type === 'not') {
        return (_jsxs("div", { className: "flex flex-col gap-2 rounded-[var(--radius-3)] border border-[var(--color-border-default)] p-3", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(Label, { className: "text-xs font-medium", children: "\u041D\u0415" }), _jsx(DeleteButton, { onClick: () => onChange(node.of), label: "\u0423\u0431\u0440\u0430\u0442\u044C \u043E\u0442\u0440\u0438\u0446\u0430\u043D\u0438\u0435" })] }), _jsx(Node, { node: node.of, depth: depth + 1, onChange: (child) => onChange(child === null ? null : { type: 'not', of: child }) })] }));
    }
    // group: all | any
    const setChild = (idx, updated) => {
        const of = node.of.flatMap((c, i) => (i === idx ? (updated === null ? [] : [updated]) : [c]));
        onChange(of.length === 0 ? null : { type: node.type, of });
    };
    const addChild = (child) => onChange({ type: node.type, of: [...node.of, child] });
    return (_jsxs("div", { className: "flex flex-col gap-3 rounded-[var(--radius-3)] border border-[var(--color-border-default)] p-3", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs(Select, { value: node.type, onValueChange: (v) => onChange({ type: v, of: node.of }), children: [_jsx(Select.Trigger, { size: "sm", "aria-label": "\u0422\u0438\u043F \u0433\u0440\u0443\u043F\u043F\u044B", className: "w-auto min-w-[8rem]", children: _jsx(Select.Value, {}) }), _jsxs(Select.Content, { children: [_jsx(Select.Item, { value: "all", children: "\u0412\u0441\u0435 \u0438\u0437" }), _jsx(Select.Item, { value: "any", children: "\u041B\u044E\u0431\u043E\u0435 \u0438\u0437" })] })] }), _jsx(DeleteButton, { onClick: () => onChange(null), label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0433\u0440\u0443\u043F\u043F\u0443" })] }), _jsx("div", { className: "flex flex-col gap-2 pl-3", children: node.of.map((child, idx) => (
                // No stable node id in `RiskReactionExpr`; index key is the same
                // documented fallback `trigger-editor.tsx` uses.
                _jsx(Node, { node: child, depth: depth + 1, onChange: (updated) => setChild(idx, updated) }, idx))) }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => addChild(defaultFieldNode()), children: [_jsx(Plus, { className: "h-3 w-3" }), " \u043F\u043E\u043B\u0435"] }), _jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => addChild({ type: 'all', of: [defaultFieldNode()] }), children: [_jsx(Plus, { className: "h-3 w-3" }), " \u0433\u0440\u0443\u043F\u043F\u0430"] }), _jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => addChild({ type: 'not', of: defaultFieldNode() }), children: [_jsx(Plus, { className: "h-3 w-3" }), " \u041D\u0415"] })] })] }));
}
/**
 * `field`-leaf editor: источник → поле (каталог `RISK_FIELD_CATALOG`) →
 * оператор (отфильтрован по операторам поля) → значение — виджет которого
 * зависит от того, подсказаны ли значения этого поля (см.
 * `SUGGESTED_VALUES`), от типа поля и от оператора.
 */
function FieldLeaf({ node, onChange, }) {
    const fields = RISK_FIELD_CATALOG[node.source];
    const def = fields.find((f) => f.path === node.path);
    const operators = def?.operators ?? [];
    const valueless = VALUELESS_OPERATORS.has(node.operator);
    const isList = node.operator === 'in';
    const suggested = suggestedValuesFor(node.source, node.path);
    const patch = (p) => onChange({ ...node, ...p });
    return (_jsxs("div", { className: "flex flex-wrap items-end gap-2 rounded-[var(--radius-3)] border border-[var(--color-border-default)] p-3", children: [_jsxs("div", { className: "flex min-w-[150px] flex-col gap-1", children: [_jsx(Label, { className: "text-xs text-[var(--color-text-muted)]", children: "\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A" }), _jsxs(Select, { value: node.source, onValueChange: (v) => 
                        // Switching source invalidates field/operator/value together.
                        onChange({
                            type: 'field',
                            source: v,
                            path: RISK_FIELD_CATALOG[v][0]?.path ?? '',
                            operator: 'equals',
                        }), children: [_jsx(Select.Trigger, { size: "sm", children: _jsx(Select.Value, {}) }), _jsx(Select.Content, { children: RISK_FIELD_SOURCES.map((s) => (_jsx(Select.Item, { value: s, children: FIELD_SOURCE_LABELS[s] }, s))) })] })] }), _jsxs("div", { className: "flex min-w-[170px] flex-col gap-1", children: [_jsx(Label, { className: "text-xs text-[var(--color-text-muted)]", children: "\u041F\u043E\u043B\u0435" }), _jsx(SearchableSelect, { "aria-label": "\u041F\u043E\u043B\u0435", size: "sm", value: node.path, onChange: (v) => 
                        // Switching field may invalidate the operator/value → reset both.
                        patch({ path: v, operator: 'equals', value: undefined }), placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043F\u043E\u043B\u0435", options: fields.map((f) => ({ value: f.path, label: f.label, keywords: [f.path] })) })] }), _jsxs("div", { className: "flex min-w-[170px] flex-col gap-1", children: [_jsx(Label, { className: "text-xs text-[var(--color-text-muted)]", children: "\u041E\u043F\u0435\u0440\u0430\u0442\u043E\u0440" }), _jsxs(Select, { value: node.operator || NONE, onValueChange: (v) => patch({ operator: v, value: undefined }), children: [_jsx(Select.Trigger, { size: "sm", children: _jsx(Select.Value, { placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043E\u043F\u0435\u0440\u0430\u0442\u043E\u0440" }) }), _jsx(Select.Content, { children: operators.map((o) => (_jsx(Select.Item, { value: o, children: FIELD_OPERATOR_LABELS[o] }, o))) })] })] }), !valueless ? (_jsxs("div", { className: "flex min-w-[170px] flex-1 flex-col gap-1", children: [_jsx(Label, { className: "text-xs text-[var(--color-text-muted)]", children: isList ? 'Значения' : 'Значение' }), _jsx(FieldValueWidget, { fieldType: def?.type ?? 'string', isList: isList, suggested: suggested, value: node.value, onChange: (v) => patch({ value: v }) })] })) : null, _jsx(DeleteButton, { onClick: () => onChange(null), label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0443\u0441\u043B\u043E\u0432\u0438\u0435" })] }));
}
/**
 * Value widget, dispatched on (a) whether the field has SUGGESTED values —
 * task 13's hard rule: `case.kind` / `alert.network` / `case.currency` /
 * `case.match_confidence` are picked from a `Select` (or a checkbox
 * multi-select for `in`), never typed free-hand — and otherwise (b) the
 * field's declared type.
 */
function FieldValueWidget({ fieldType, isList, suggested, value, onChange, }) {
    if (suggested) {
        return isList ? (_jsx(EnumMultiSelect, { options: suggested, selected: Array.isArray(value) ? value : [], onChange: onChange })) : (_jsx(SearchableSelect, { "aria-label": "\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u0435", size: "sm", value: typeof value === 'string' ? value : '', onChange: (v) => onChange(v), placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435", options: suggested.map((o) => ({ value: o.value, label: o.label, keywords: [o.value] })) }));
    }
    if (isList) {
        // Free-text list, comma-separated — same convention as the pipeline
        // editor's `field_match` value input. Empty/unparseable numeric tokens
        // are dropped rather than coerced to `NaN`.
        const raw = Array.isArray(value) ? value.join(', ') : '';
        return (_jsx(Input, { value: raw, placeholder: "\u0447\u0435\u0440\u0435\u0437 \u0437\u0430\u043F\u044F\u0442\u0443\u044E", onChange: (e) => {
                const parts = e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                if (fieldType === 'number') {
                    const nums = parts.map((s) => Number(s)).filter((n) => Number.isFinite(n));
                    onChange(nums.length > 0 ? nums : undefined);
                }
                else {
                    onChange(parts.length > 0 ? parts : undefined);
                }
            } }));
    }
    if (fieldType === 'number') {
        return (_jsx(Input, { type: "number", value: typeof value === 'number' ? String(value) : '', onChange: (e) => {
                const raw = e.target.value;
                if (raw.trim() === '') {
                    onChange(undefined);
                    return;
                }
                const num = Number(raw);
                // A transient invalid keystroke (bare "-", trailing ".") keeps the
                // last valid value instead of writing `NaN` into the tree — `NaN`
                // is a `number` and would pass validation while never matching.
                if (Number.isFinite(num))
                    onChange(num);
            } }));
    }
    return (_jsx(Input, { value: typeof value === 'string' ? value : '', onChange: (e) => onChange(e.target.value) }));
}
/**
 * Multi-select for the `in` operator on a suggested-values field — the kit
 * `Combobox` (multiple), the same widget `ui-forms`' `MultiSelectWidget`
 * renders: chips inside the input row, typing filters (by label and code).
 * The stored value is the same `string[]` as before.
 */
function EnumMultiSelect({ options, selected, onChange, }) {
    return (_jsx(Combobox, { multiple: true, "aria-label": "\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u044F", placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435\u2026", options: options.map((o) => ({ value: o.value, label: o.label, keywords: [o.value] })), value: selected, onChange: onChange }));
}
function DeleteButton({ onClick, label }) {
    return (_jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: onClick, "aria-label": label, className: "text-[var(--color-state-danger-fg)]", children: _jsx(X, { className: "h-4 w-4" }) }));
}
//# sourceMappingURL=reaction-condition-editor.js.map